import { ComponentRef, EmbeddedViewRef, Injectable, ComponentFactoryResolver, ApplicationRef, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { environment } from '../environments/environment';
import { Router } from '@angular/router';
import { LoginResponse, RenovarTokenRequest } from '../interfaces/auth/login.interface';
import { LoginRequest } from '../interfaces/auth/login.interface';
import { Saldo } from '../interfaces/saldo/saldo.interface';
import { BaseService } from '../infra/base-service';
import { BaseStateService } from '../state-service/base-state.service';
import { SaldoService } from './saldo.service';
import { NumberUtil } from '../infra/number-util';
import { ModalRenovarSessaoComponent } from '../modules/area-logada/shared/renovar-sessao/modal-renovar-sessao.component';
import { ToastrService } from 'ngx-toastr';
import { EsqueciSenhaComponent } from '../modules/acesso/login/esqueci-senha/esqueci-senha.component';
import { EsqueciSenhaRequest, EsqueciSenhaResponse } from '../interfaces/auth/esqueci-senha.interface';
@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseService {
  private readonly TOKEN_KEY = environment.tokenKey;
  private readonly SALDO_KEY = environment.saldoKey;
  private readonly SALDO_TIMESTAMP_KEY = environment.saldoTimestampKey;
  private readonly NOME_USUARIO_KEY = environment.nomeUsuarioKey;
  private readonly NOME_CLIENTE_KEY = environment.nomeClienteKey;
  private readonly IP_USUARIO_KEY = environment.ipUsuarioKey;
  private readonly PRIMEIRO_ACESSO_KEY = environment.primeiroAcessoKey;
  private readonly TIPO_CLIENTE_KEY = environment.tipoClienteKey;
  private renovarTokenEmAndamento = false;

  private saldoInicial: Saldo = {
    quantidade: 0,
    motivo: 0,
    mensagemMotivoSaldo: '',
    tipo: '',
  };

  private isAuthenticatedSubject: BehaviorSubject<boolean>;
  private saldoSubject: BehaviorSubject<Saldo>;

  public isAuthenticated$: Observable<boolean>;
  public saldo$: Observable<Saldo>;
  private modalComponentRef: ComponentRef<ModalRenovarSessaoComponent> | null = null;
  constructor(
    private baseStateService: BaseStateService,
    private router: Router,
    http: HttpClient,
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private appRef: ApplicationRef,
    protected override toastr: ToastrService
  ) {
    super(http, toastr);
    this.isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
    this.saldoSubject = new BehaviorSubject<Saldo>(this.saldoInicial);

    this.isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
    this.saldo$ = this.saldoSubject.asObservable();
  }

  private getSaldoService(): SaldoService {
    return this.injector.get(SaldoService);
  }  

  async login(credentials: LoginRequest): Promise<LoginResponse | null> {
    try {
      const response = await this.post<LoginResponse>(`acesso/logar`, credentials);
      this.setSession(response, false);
      return response;
    } catch (error: any) {
      console.error('Erro ao fazer login:', error);
      return null;
    }
  }

  verificarTokenValido(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    return this.tempoRestanteToken() > 0;
  }

  logout(): void {
    this.baseStateService.removeItem(this.TOKEN_KEY);
    this.baseStateService.removeItem(this.SALDO_KEY);
    this.baseStateService.removeItem(this.SALDO_TIMESTAMP_KEY);
    this.baseStateService.removeItem(this.NOME_USUARIO_KEY);
    this.baseStateService.removeItem(this.IP_USUARIO_KEY);
    this.baseStateService.removeItem(environment.filtroSegmentacaoKey);
    this.baseStateService.removeItem(environment.todasPermissoesKey);
    this.baseStateService.removeItem(environment.todasPermissoesTempoExpiracaoKey); 
    this.baseStateService.removeItem('modalSemCreditosExibido');
    this.baseStateService.removeItem('modalBoasVindasExibido');
    this.baseStateService.removeItem('listaContatos');
    this.baseStateService.removeItem('listaSalvaPagina');
    this.baseStateService.removeItem(this.PRIMEIRO_ACESSO_KEY);
    this.baseStateService.removeItem(this.TIPO_CLIENTE_KEY);
    
    this.getSaldoService().resetarSaldo();

    this.isAuthenticatedSubject.next(false);
    this.saldoSubject.next(this.saldoInicial);

    this.router.navigate(['/login']);
  }

  async renovarToken(): Promise<boolean> {
    const token = this.getToken();
    if (!token) {
      this.logout();
      return false;
    }
  
    try {
      const response = await this.post<LoginResponse>(`acesso/refreshtoken`, null);
      this.setSession(response, true);
      return true;
    } catch (error) {
      this.logout();
      return false;
    }
  }

  async atualizarSaldo(): Promise<Saldo> {
    if (!this.hasToken()) {
      return this.saldoInicial;
    }

    var saldo = await this.getSaldoService().obterSaldoAtual()
    this.baseStateService.setItem(this.SALDO_KEY, saldo.toString());
    this.baseStateService.setItem(this.SALDO_TIMESTAMP_KEY, new Date().getTime().toString());
    this.saldoSubject.next(saldo);

    return saldo;
  }
  
  hasPermission(permission: number): boolean {
    const permissoes = this.getPermissoes();
    return permissoes.includes(permission) || this.getIsMaster();
  }

  verificarTokenExpiracao(): void {
    if (this.renovarTokenEmAndamento) {
      return;
    } 
    this.renovarTokenEmAndamento = true;
    const token = this.getToken();
    if (!token) {
      return;
    }

    try {      
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        return;
      }

      const tempoRestante = this.tempoRestanteToken();
      const tempoMinimoRenovacao = NumberUtil.converterMinutosEmMilissegundos(environment.tempoMinimoRenovacaoTokenMinutos);
      const tempoAlertaExpiracao = NumberUtil.converterMinutosEmMilissegundos(environment.tempoAlertaExpiracaoTokenMinutos);

      if (tempoRestante < tempoMinimoRenovacao && tempoRestante > 0) {
        this.renovarToken();
      }
      if (tempoRestante < tempoAlertaExpiracao && tempoRestante > 0) {
        this.exibirAlertaExpiracao();
      }      
    } catch (error) {
      console.error('Erro ao decodificar token:', error);
    } finally {
      this.renovarTokenEmAndamento = false;      
    }
  }

  tempoRestanteToken(): number {
    const token = this.getToken();
    if (!token) {
      return 0;
    }

    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      return 0;
    }

    const payload = JSON.parse(atob(tokenParts[1]));  
    const expiracaoTimestamp = payload.exp * 1000;
    const agora = new Date().getTime();
    const tempoRestante = expiracaoTimestamp - agora;
    return tempoRestante;
  } 

  private async exibirAlertaExpiracao(): Promise<void>     {
    try {
      if (this.modalComponentRef) {
        this.appRef.detachView(this.modalComponentRef.hostView);
        this.modalComponentRef.destroy();
        this.modalComponentRef = null;
      }

      const componentFactory = this.componentFactoryResolver
        .resolveComponentFactory(ModalRenovarSessaoComponent);
      this.modalComponentRef = componentFactory.create(this.injector);

      this.appRef.attachView(this.modalComponentRef.hostView);
      const domElem = (this.modalComponentRef.hostView as EmbeddedViewRef<any>)
        .rootNodes[0] as HTMLElement;
      document.body.appendChild(domElem);

      const renovar = await this.modalComponentRef.instance.mostrar(environment.tempoConfirmarRenovarSessaoSegundos);

      if (renovar) {
        await this.renovarToken();
      } else {
        this.logout();
      }

    } catch (error) {
      this.logout()
    } finally {
      if (this.modalComponentRef) {
        this.appRef.detachView(this.modalComponentRef.hostView);
        this.modalComponentRef.destroy();
        this.modalComponentRef = null;
      }
    }
  }

  private setSession(response: LoginResponse, isRefreshToken: boolean = false): void {
    this.baseStateService.setItem(this.TOKEN_KEY, response.token);

    if(!isRefreshToken){
      this.baseStateService.setItem(this.NOME_USUARIO_KEY, response.nome);
      this.baseStateService.setItem(this.IP_USUARIO_KEY, response.ip);
      this.baseStateService.setItem(this.PRIMEIRO_ACESSO_KEY, response.primeiroAcesso.toString());
      this.baseStateService.setItem(this.TIPO_CLIENTE_KEY, response.tipoCliente);
    }    

    this.atualizarSaldo();

    this.isAuthenticatedSubject.next(true);    
  }

  private hasToken(): boolean {
    return !!this.baseStateService.getItem(this.TOKEN_KEY);
  }

  getToken(): string | null {
    return this.baseStateService.getItem(this.TOKEN_KEY);
  }

  getSaldo(): Saldo {
    const saldoStr = this.baseStateService.getItem(this.SALDO_KEY);
    return saldoStr ? JSON.parse(saldoStr) : this.saldoInicial;
  }

  getPermissoes(): number[] {
    const token = this.getToken();
    if (!token) return [];

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.per;
    } catch {
      return [];
    }
  }

  getIsMaster(): boolean {
    const token = this.getToken();
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.uma === 'True';
    } catch {
      return false;
    }
  }

  getNomeUsuario(): string {
    return this.baseStateService.getItem(this.NOME_USUARIO_KEY) || '-';
  }

  getIpUsuario(): string {
    return this.baseStateService.getItem(this.IP_USUARIO_KEY) || 'Não disponível';
  }

  getIdCliente(): number {
    const token = this.getToken();
    if (!token) return 0;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.cid;
    } catch {
      return 0;
    }
  }

  getIdUsuarioLogado(): number {
    const token = this.getToken();
    if (!token) return 0;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.uid;
    } catch {
      return 0;
    }
  }

  getPrimeiroAcesso(): boolean {
    const primeiroAcesso = this.baseStateService.getItem(this.PRIMEIRO_ACESSO_KEY);
    if (primeiroAcesso === null) {
      return true;
    }
    return primeiroAcesso === 'true';
  }

  getTipoCliente(): string {
    try {
      let tipoCliente = this.baseStateService.getItem(this.TIPO_CLIENTE_KEY);
      return tipoCliente ? tipoCliente : '';
    } catch {
      return '';
    }
  }

  obterCodigoRecuperacao(model: EsqueciSenhaRequest): Promise<EsqueciSenhaResponse> {
    return this.post<EsqueciSenhaResponse>('acesso/esquecisenha', model);
  }

  recuperarSenha(model: EsqueciSenhaRequest): Promise<void> {
    return this.post<void>('acesso/trocarsenha', model);
  }
}